import styled from 'styled-components'

export const NoiseContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.large};
`

export const NoiseSourcesContainer = styled.div`
  margin: 0 ${({ theme }) => theme.spacing.large};
`

export const NoiseOverview = styled.div`
  display: grid;
  margin: 0 ${({ theme }) => theme.spacing.large};
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.large};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.medium};
  }
`

export const NoiseCard = styled.div<{ $period: 'day' | 'night' }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.default};
  }
`

export const NoiseCardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const TimeIndicator = styled.div<{ $period: 'day' | 'night' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  color: ${({ theme, $period }) =>
    $period === 'day' ? theme.colors.warning : '#7b61ff'};
`

export const TimeLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.uppercase};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const NoiseValueDisplay = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`

export const NoiseValue = styled.span<{ $period: 'day' | 'night' }>`
  font-size: 36px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  line-height: 1;
  color: ${({ theme, $period }) =>
    $period === 'day' ? theme.colors.warning : '#7b61ff'};
`

export const NoiseUnit = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const NoiseCardBody = styled.div`
  padding: 20px 24px;
`

export const NoiseDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const NoiseSources = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.default};
`

export const NoiseSource = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

export const SourceIcon = styled.div`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex-shrink: 0;
`

export const SourceLabel = styled.span`
  flex: 1;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const SourceValue = styled.span<{ $level: 'low' | 'moderate' | 'high' }>`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme, $level }) => {
    switch ($level) {
      case 'high':
        return theme.colors.warning
      case 'moderate':
        return '#ff9500'
      default:
        return theme.colors.text.primary
    }
  }};
`

export const DecibelScaleCard = styled.div`
  margin: 0 ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 20px;
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const ScaleTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const VisualScaleIntegrated = styled.div`
  position: relative;
  margin-bottom: 20px;
  padding-top: 32px;
`

export const ScaleGradient = styled.div`
  height: 12px;
  background: linear-gradient(
    to right,
    #00b341 0%,
    #00b341 20%,
    #ffb800 30%,
    #ff9500 60%,
    #ff6b35 80%,
    #ff3b30 100%
  );
  border-radius: 6px;
  margin-bottom: 8px;
`

export const ScaleLabels = styled.div`
  position: relative;
  height: 30px;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ScaleLabelItem = styled.div<{ $left: string }>`
  position: absolute;
  left: ${({ $left }) => $left};
  transform: translateX(-50%);
  text-align: center;
`

export const DbValue = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
`

export const DbDesc = styled.span`
  display: block;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: 2px;
`

export const ScaleIndicators = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 44px;
`

export const Indicator = styled.div<{
  $left: string
  $period: 'day' | 'night'
}>`
  position: absolute;
  left: ${({ $left }) => $left};
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  transform: translateX(-50%);
`

export const IndicatorLine = styled.div<{ $period: 'day' | 'night' }>`
  width: 2px;
  height: 16px;
  background: ${({ theme, $period }) =>
    $period === 'day' ? theme.colors.warning : '#7B61FF'};
  margin-top: 2px;
`

export const IndicatorLabel = styled.div<{ $period: 'day' | 'night' }>`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme, $period }) =>
    $period === 'day' ? theme.colors.warning : '#7B61FF'};
  background: ${({ $period }) =>
    $period === 'day' ? 'rgba(255, 149, 0, 0.1)' : 'rgba(123, 97, 255, 0.1)'};
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  white-space: nowrap;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`

export const ScaleReferenceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.small};
  }
`

export const ReferenceColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

export const ReferenceItem = styled.div<{ $highlight?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: 8px 12px;
  background: ${({ theme, $highlight }) =>
    $highlight ? 'rgba(255, 149, 0, 0.1)' : theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`

export const RefValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 45px;
`

export const RefExample = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.secondary};
  flex: 1;
`

export const NoiseLoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.huge};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const NoiseErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.huge};
  color: ${({ theme }) => theme.colors.danger};
  background: rgba(255, 59, 48, 0.05);
  border: 1px solid rgba(255, 59, 48, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  text-align: center;
`

export const NoiseErrorMessage = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const NoiseErrorDetails = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`
